import React from "react";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import { Portlet, PortletBody, PortletHeader } from "../../../partials/content/Portlet";
import DataGridBase from "../../../partials/layout/DataGridBase";
import BiDynamicForm from "../../../partials/layout/BiDynamicForm";
import SimpleBox from "../../../partials/content/SimpleBox";
import * as searchCrud from "../../../crud/search.crud";
import * as contractsCrud from "../../../crud/contracts.crud";
import Tooltip from "devextreme-react/tooltip";
import { exportDataGrid } from "devextreme/excel_exporter";
import ExcelJS from "exceljs";
import saveAs from "file-saver";

class SearchGrid extends React.Component {
  constructor(props) {
    super(props);

    this.entityTypes = [
      { value: "", display: this.props.intl.formatMessage({ id: "SEARCHGRID.ENTITY.ALL" }) },
      { value: "Contract", display: this.props.intl.formatMessage({ id: "SEARCHGRID.ENTITY.CONTRACTS" }) },
      { value: "Project", display: this.props.intl.formatMessage({ id: "SEARCHGRID.ENTITY.PROJECTS" }) },
      { value: "Company", display: this.props.intl.formatMessage({ id: "SEARCHGRID.ENTITY.COMPANIES" }) },
      { value: "File", display: this.props.intl.formatMessage({ id: "SEARCHGRID.ENTITY.FILES" }) },
      { value: "Notification", display: this.props.intl.formatMessage({ id: "SEARCHGRID.ENTITY.NOTIFICATIONS" }) }
    ];

    this.state = {
      loading: false,
      data: [],
      totalResults: 0,
      contractOptions: [],
      searchError: "",
      formValues: {
        searchValue: "",
        entityType: "",
        contractId: null,
        isExactMatch: false
      }
    };

    this.formConfig = {
      buttonText: this.props.intl.formatMessage({ id: "SEARCHGRID.SEARCH_BUTTON" }),
      fields: [
        {
          label: this.props.intl.formatMessage({ id: "NAVIGATION.MENU.SEARCH" }),
          dataField: "searchValue",
          editorType: "dxTextBox"
        },
        {
          label: this.props.intl.formatMessage({ id: "SEARCHGRID.COLUMN.CONTRACT" }),
          dataField: "contractId",
          required: false,
          editorType: "dxSelectBox",
          editorOptions: {
            showClearButton: true,
            dataSource: [],
            valueExpr: "contractid",
            displayExpr: "name"
          }
        },
        {
          label: this.props.intl.formatMessage({ id: "SEARCHGRID.COLUMN.TYPE" }),
          dataField: "entityType",
          required: false,
          editorType: "dxSelectBox",
          editorOptions: {
            showClearButton: true,
            dataSource: this.entityTypes,
            valueExpr: "value",
            displayExpr: "display"
          }
        },
        {
          label: this.props.intl.formatMessage({ id: "SEARCHGRID.EXACT_MATCH" }),
          dataField: "isExactMatch",
          editorType: "dxSwitch",
          editorOptions: {
            switchedOnText: this.props.intl.formatMessage({ id: "GENERAL.FORMS.SWITCH_YES" }),
            switchedOffText: this.props.intl.formatMessage({ id: "GENERAL.FORMS.SWITCH_NO" })
          }
        }
      ]
    };
  }

  componentDidMount() {
    this.loadContracts();
  }

  loadContracts = async () => {
    try {
      const response = await contractsCrud.toSelectList();
      const contracts = response.data || [];

      this.setState({ contractOptions: contracts });

      const contractField = this.formConfig.fields.find(f => f.dataField === "contractId");
      if (contractField) {
        contractField.editorOptions.dataSource = contracts;
      }
    } catch (err) {
      console.error("Failed to load contracts", err);
    }
  };

  openResult = (row) => {
    if (!row.url) return;

    this.setState(prev => ({
      data: prev.data.map(item =>
        item.entityId === row.entityId
          ? { ...item, visited: true }
          : item
      )
    }));

    window.open(row.url, "_blank", "noopener,noreferrer");
  };

  onFormValueChanged = (field, value) => {
    this.setState(prev => {
      const updatedFormValues = {
        ...prev.formValues,
        [field]: value
      };

      let searchError = prev.searchError;

      if (field === "searchValue") {
        const query = (value || "").trim();

        if (!query) {
          searchError = "";
        } else if (query.length < 4) {
          searchError = this.props.intl.formatMessage({ id: "SEARCHGRID.MIN_SEARCH_LENGTH" });
        } else {
          searchError = "";
        }
      }

      return {
        formValues: updatedFormValues,
        searchError
      };
    });
  };

  runSearch = async (formObj, setSubmitting) => {
    const query = (formObj.searchValue || "").trim();

    this.setState({ formValues: formObj });

    if (!query) {
      this.setState({
        searchError: this.props.intl.formatMessage({ id: "SEARCHGRID.SEARCH_REQUIRED" }),
        data: [],
        totalResults: 0
      });

      setSubmitting(false);
      return;
    }

    if (query.length < 4) {
      this.setState({
        searchError: this.props.intl.formatMessage({ id: "SEARCHGRID.MIN_SEARCH_LENGTH" }),
        data: [],
        totalResults: 0
      });

      setSubmitting(false);
      return;
    }

    this.setState({
      searchError: "",
      loading: true,
      data: [],
      totalResults: 0
    });

    try {
      const response = await searchCrud.globalSearch(
        query,
        formObj.entityType || null,
        500,
        0,
        formObj.contractId || null,
        !!formObj.isExactMatch
      );

      const payload = response.data || {};
      const results = payload.Results || [];
      const total = payload.Total || results.length;

      const mapped = results.map((r, idx) => ({
        resultNo: idx + 1,
        entityId: r.EntityID,
        entityType: r.EntityType,
        contractName: r.ContractName,
        projectName: r.ProjectName,
        companyName: r.CompanyName,
        reference: r.Reference,
        subject: r.Subject,
        updatedAt: r.SourceUpdatedAtUtc,
        url: r.Url,
        visited: false
      }));

      this.setState({
        data: mapped,
        totalResults: total,
        loading: false,
        searchError: ""
      });

      setSubmitting(false);
    } catch (err) {
      this.setState({
        data: [],
        loading: false,
        totalResults: 0
      });

      setSubmitting(false);
    }
  };

  onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Global Search");

    exportDataGrid({
      component: e.component,
      worksheet: worksheet,
      customizeCell: (options) => {
        const { gridCell, excelCell } = options;

        if (gridCell.rowType === "totalFooter") {
          excelCell.value = "";
          return;
        }

        if (gridCell.rowType === "header" && gridCell.column.dataField === "entityId") {
          excelCell.value = "Link";
        }

        if (gridCell.rowType === "data") {
          if (gridCell.column.dataField === "entityId") {
            const rowData = gridCell.data;

            if (rowData.url) {
              excelCell.value = {
                text: "Open",
                hyperlink: rowData.url
              };

              excelCell.font = { color: { argb: "FF0000FF" }, underline: true };
              excelCell.alignment = { horizontal: "left", vertical: "top" };
            }
          }
        }
      }
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "GlobalSearch.xlsx"
        );
      });
    });

    e.cancel = true;
  };

  render() {
    const { intl } = this.props;
    const t = (id) => intl.formatMessage({ id });

    const columns = [
      { caption: "#", dataField: "resultNo", width: 70, allowSorting: false, allowFiltering: false },
      {
        caption: t("SEARCHGRID.VIEW"),
        dataField: "entityId",
        width: 100,
        allowFiltering: false,
        allowSorting: false,
        cellRender: (cell) => {
          const isVisited = !!cell.data.visited;

          return (
            <span
              onClick={() => this.openResult(cell.data)}
              className={isVisited ? "text-muted font-weight-bold" : "text-primary font-weight-bold"}
              style={{ cursor: "pointer" }}
            >
              {t("SEARCHGRID.VIEW")}
            </span>
          );
        }
      },
      { caption: t("SEARCHGRID.COLUMN.TYPE"), dataField: "entityType" },
      { caption: t("SEARCHGRID.COLUMN.CONTRACT"), dataField: "contractName" },
      { caption: t("SEARCHGRID.COLUMN.PROJECT"), dataField: "projectName" },
      { caption: t("SEARCHGRID.COLUMN.COMPANY"), dataField: "companyName" },
      { caption: t("SEARCHGRID.COLUMN.REFERENCE"), dataField: "reference" },
      { caption: t("SEARCHGRID.COLUMN.SUBJECT"), dataField: "subject" },
      { caption: t("SEARCHGRID.COLUMN.URL"), dataField: "url", visible: false, allowHiding: true }
    ];

    return (
      <Portlet>
        <div style={{ position: "relative" }}>
          <PortletHeader title={t("SEARCHGRID.TITLE")} />

          <span
            id="searchTipsButton"
            className="dx-icon dx-icon-info"
            style={{ position: "absolute", top: 18, left: 140, cursor: "pointer", fontSize: 16 }}
          />
        </div>

        <PortletBody>
          <Tooltip target="#searchTipsButton" showEvent="mouseenter" hideEvent="mouseleave" position="right">
            <div style={{ maxWidth: 500, textAlign: "left", fontSize: 14 }}>
              <ul className="mb-2">
                <li>{t("SEARCHGRID.TIP_WORDS")}</li>
                <li>{t("SEARCHGRID.TIP_OPERATORS")}</li>
                <li>{t("SEARCHGRID.TIP_PARENTHESES")}</li>
                <li>{t("SEARCHGRID.TIP_PUNCTUATION")}</li>
                <li>{t("SEARCHGRID.TIP_EXACT_MATCH")}</li>
              </ul>

              <div className="font-weight-bold mb-1">{t("SEARCHGRID.EXAMPLES")}</div>

              <ul className="mb-0">
                <li>{t("SEARCHGRID.EXAMPLE_1")}</li>
                <li>{t("SEARCHGRID.EXAMPLE_2")}</li>
              </ul>
            </div>
          </Tooltip>

          <SimpleBox title={t("SEARCHGRID.SEARCH_BOX_TITLE")}>
            <BiDynamicForm
              config={this.formConfig}
              initialValues={this.state.formValues}
              valueChangeHandler={this.onFormValueChanged}
              onSubmitHandler={this.runSearch}
            />

            {this.state.searchError && (
              <div style={{ color: "#d9534f", fontSize: "12px", marginTop: "8px" }}>
                {this.state.searchError}
              </div>
            )}
          </SimpleBox>

          <div style={{ height: 20 }} />

          <SimpleBox title={t("SEARCHGRID.RESULTS")}>
            <DataGridBase
              version="8"
              name="GlobalSearch"
              dataSource={this.state.data}
              keyExpr="entityId"
              hoverStateEnabled={true}
              repaintAfterMounted={true}
              columns={columns}
              columnChooser={{ enabled: true, mode: "select", height: 400, width: 300 }}
              onExporting={this.onExporting}
              export={{ enabled: true, fileName: "GlobalSearch" }}
              summary={{
                totalItems: [
                  {
                    column: "entityType",
                    summaryType: "count",
                    customizeText: (e) => {
                      if (this.state.totalResults > 500) return "Count: 500+";
                      return `Count: ${e.value}`;
                    }
                  }
                ]
              }}
            />
          </SimpleBox>
        </PortletBody>
      </Portlet>
    );
  }
}

export default injectIntl(connect()(SearchGrid));